import { useState, useEffect } from 'react';
import styles from '../assets/styles/frcdeal.module.css'
import Backarrow from '../components/Backarrow';

// Falls back to the production deployment if the env var isn't set (e.g. a
// fresh clone without .env changes carried over).
const FRC_DEAL_URL = process.env.REACT_APP_FRC_DEAL_URL || "https://frc-deal.vercel.app";
const FRC_DEAL_ORIGIN = new URL(FRC_DEAL_URL).origin;

// FRC Deal's own /offline route (no Firebase, plays vs bots — see its
// web/src/app/offline/page.tsx) is a fully static page that FRC Deal's own
// service worker caches, so it can load with zero network as long as this
// device visited FRC Deal at least once while online (see
// FrcDealPreload.js, which tries to guarantee that ahead of time).
const FRC_DEAL_OFFLINE_URL = `${FRC_DEAL_URL}/offline`;

// How long to wait for FRC Deal's "I actually rendered" ping before assuming
// this device never warmed its cache and falling back to the message.
const OFFLINE_READY_TIMEOUT_MS = 5000;

/** Embeds the FRC Deal card game (a separate Team 3128 app) in an iframe.
 *  Online, this is the full multiplayer lobby. Offline, it points at FRC
 *  Deal's own bots-only /offline route instead — that only works if this
 *  device has visited FRC Deal before (so its service worker had a chance to
 *  cache it).
 *
 *  The offline case can't use the iframe's `load` event to know whether it
 *  worked: a cross-origin iframe fires `load` even for the browser's own
 *  "can't reach this page" error, so a dead/never-cached target looks
 *  identical to a real success. Instead FRC Deal's own app pings
 *  window.parent once it has genuinely mounted (see its
 *  ServiceWorkerRegister.tsx) — only real rendered FRC Deal code can send
 *  that, so we wait for it (with a timeout) before trusting the iframe. */
function FrcDeal({ currentTab, setCurrentTab, onlineStatus }) {
    const [loaded, setLoaded] = useState(false);
    const [offlineReady, setOfflineReady] = useState(false);
    const [offlineTimedOut, setOfflineTimedOut] = useState(false);

    // Reset per-visit state each time this tab is (re)entered, so leaving
    // and coming back doesn't show stale state from a prior mount.
    useEffect(() => {
        if (currentTab === "frcdeal") {
            setLoaded(false);
            setOfflineReady(false);
            setOfflineTimedOut(false);
        }
    }, [currentTab]);

    useEffect(() => {
        if (currentTab !== "frcdeal" || onlineStatus) return;
        const onMessage = (event) => {
            if (event.origin === FRC_DEAL_ORIGIN && event.data && event.data.source === 'frc-deal') {
                setOfflineReady(true);
            }
        };
        window.addEventListener('message', onMessage);
        const timer = setTimeout(() => setOfflineTimedOut(true), OFFLINE_READY_TIMEOUT_MS);
        return () => {
            window.removeEventListener('message', onMessage);
            clearTimeout(timer);
        };
    }, [currentTab, onlineStatus]);

    if (currentTab !== "frcdeal") return;

    const showBlockedMessage = !onlineStatus && offlineTimedOut && !offlineReady;

    return (
        <div className={`subpage ${styles.frcDealPage}`}>
            {showBlockedMessage ? (
                <div className={styles.offlineBox}>
                    <span>Couldn't load the offline game.</span>
                    <span>Connect once first, then it'll work offline too.</span>
                </div>
            ) : (
                <>
                    {!loaded && <div className={styles.loadingOverlay}>Loading FRC Deal…</div>}
                    <iframe
                        className={styles.frame}
                        src={onlineStatus ? FRC_DEAL_URL : FRC_DEAL_OFFLINE_URL}
                        title="FRC Deal"
                        onLoad={() => setLoaded(true)}
                        allow="autoplay"
                    />
                </>
            )}

            <Backarrow setCurrentTab={setCurrentTab} />
        </div>
    );
}
export default FrcDeal;
